import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AirVent, ArrowLeft, Wand2, Eye, Fingerprint, ImagePlus, Info, Sparkles, Users2, ListChecks, MapPin, UtensilsCrossed, Clock3, Flame, Trophy, Radio, BadgeCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { useToast } from "@/hooks/use-toast";
import type { KarmaCircleRule, KarmaCircleRuleSet } from "@/lib/karma/types";
import { getShopBundleForCode } from "@/lib/karma/shop";
import {
  createCircleInDb,
  createCircleTaskInDb,
  deleteCircleTaskInDb,
  estimateCircleMatchCount,
  getMyCircleDraftByIdInDb,
  getMyLatestCircleDraftInDb,
  listCircleTasks,
  publishCircleDraftInDb,
  saveCircleDraftInDb,
  type CircleTaskKind,
} from "@/lib/supabase/circles";
import { createRitualSessionFromCircle } from "@/lib/supabase/rituals";

type StepKey = "basic" | "filters" | "activity" | "tasks" | "preview";

const STEP_ORDER: StepKey[] = ["basic", "filters", "activity", "tasks", "preview"];
const STEP_LABELS: Record<StepKey, string> = {
  basic: "Temel Bilgiler",
  filters: "Numerolojik Filtreler",
  activity: "Aktivite Seçimi",
  tasks: "Görevler",
  preview: "Önizleme",
};

type ActivityKey = "meditasyon" | "nefes" | "rituel" | "sohbet" | "diyet";

const ACTIVITY_LABELS: Record<ActivityKey, string> = {
  meditasyon: "Meditasyon",
  nefes: "Nefes",
  rituel: "Ritüel",
  sohbet: "Sohbet",
  diyet: "Diyet",
};

type CircleType = "Meditasyon Çemberi" | "Şifa Çemberi" | "Bolluk Çemberi" | "Aşk Çemberi" | "Karmik Temizleme";

const CIRCLE_TYPES: CircleType[] = ["Meditasyon Çemberi", "Şifa Çemberi", "Bolluk Çemberi", "Aşk Çemberi", "Karmik Temizleme"];

type CircleDurationOption = "7" | "21" | "40" | "unlimited";
type ParticipationRuleOption = "7" | "14" | "21" | "unlimited";

const CIRCLE_DURATION_OPTIONS: Array<{ value: CircleDurationOption; label: string }> = [
  { value: "7", label: "7 Gün" },
  { value: "21", label: "21 Gün" },
  { value: "40", label: "40 Gün" },
  { value: "unlimited", label: "Süresiz" },
];

type TaskFrequency = "daily" | "weekly3" | "weekly1";

const TASK_FREQUENCY_OPTIONS: Array<{ value: TaskFrequency; label: string }> = [
  { value: "daily", label: "Her gün" },
  { value: "weekly3", label: "Haftada 3 gün" },
  { value: "weekly1", label: "Haftada 1 gün" },
];

const TASK_DURATION_OPTIONS = [5, 10, 20, 30] as const;

const TASK_FREQUENCY_LABELS: Record<TaskFrequency, string> = {
  daily: "Her gün",
  weekly3: "Haftada 3 gün",
  weekly1: "Haftada 1 gün",
};

type CircleAura = "sifa" | "bolluk" | "denge";

const CIRCLE_AURA_OPTIONS: Array<{ value: CircleAura; label: string; hz: number }> = [
  { value: "sifa", label: "Şifa Alanı", hz: 528 },
  { value: "bolluk", label: "Bolluk Akışı", hz: 639 },
  { value: "denge", label: "Denge Frekansı", hz: 432 },
];

const RITUAL_HOUR_OPTIONS = ["06:00", "07:30", "12:00", "21:00", "22:22"] as const;

const BADGE_PREVIEWS = ["İlk Ritüel", "7 Günlük Seri", "21 Günlük Ustalık"] as const;

const PARTICIPATION_RULE_OPTIONS: Array<{ value: ParticipationRuleOption; label: string }> = [
  { value: "7", label: "7 Gün" },
  { value: "14", label: "14 Gün" },
  { value: "21", label: "21 Gün" },
  { value: "unlimited", label: "Sınırsız" },
];

const PROGRAM_LIST_PAGE_SIZE = 6;

type CircleTaskDraft = {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  kind: CircleTaskKind;
  points: number;
  frequency: TaskFrequency;
  durationMinutes: number;
  active: boolean;
};

type ActivitySuggestion = {
  id: string;
  activity: ActivityKey;
  karmicCode: number;
  karmicCodes: number[];
  title: string;
  description: string;
  kind: CircleTaskKind;
  points: number;
  audioUrl?: string;
  imageUrl?: string;
  videoUrl?: string;
  durationMinutes?: number;
};

type ProgramOption = {
  id: string;
  title: string;
  description: string;
  durationMinutes?: number;
  imageUrl?: string;
  audioUrl?: string;
  videoUrl?: string;
};

type MeditationLibraryItem = {
  id: string;
  title: string;
  description: string;
  durationMinutes: number;
  audioUrl?: string;
  active: boolean;
  tags: string[];
};

type MeditationLibraryResponse = {
  ok: true;
  items: MeditationLibraryItem[];
};

type DietLibraryItem = {
  id: string;
  title: string;
  description: string;
  coverImageUrl?: string;
  tags?: string[];
  active: boolean;
};

type DietLibraryResponse = {
  ok: true;
  items: DietLibraryItem[];
};

type ActivityLibraryItem = {
  id: string;
  title: string;
  description: string;
  durationMinutes?: number;
  active: boolean;
  tags: string[];
};

type ActivityLibraryResponse = {
  ok: true;
  items: ActivityLibraryItem[];
};

function normalizeTag(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z]+/g, "");
}

function toPublicUrl(rawValue?: string) {
  const raw = String(rawValue ?? "").trim();
  if (!raw) return "";
  if (raw.startsWith("data:") || raw.startsWith("blob:") || raw.startsWith("/")) return raw;
  if (raw.startsWith("//")) return `https:${raw}`;
  if (raw.startsWith("ftp://")) return raw.replace(/^ftp:\/\//i, "https://");
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  return `https://${raw}`;
}

function hasDirectAudioUrl(rawUrl?: string) {
  const url = String(rawUrl ?? "").trim().toLocaleLowerCase("tr-TR");
  if (!url) return false;
  return !url.includes("youtube.com") && !url.includes("youtu.be");
}

function hasDirectVideoUrl(rawUrl?: string) {
  const url = String(rawUrl ?? "").trim().toLocaleLowerCase("tr-TR");
  if (!url) return false;
  if (url.includes("youtube.com") || url.includes("youtu.be")) return false;
  return url.endsWith(".mp4") || url.endsWith(".webm") || url.endsWith(".ogg") || url.includes("video");
}

function matchesKarmicCodeInSource(source: { title: string; description: string; tags?: string[] }, code: number) {
  const normalizedTags = (source.tags ?? []).map(normalizeTag).filter(Boolean);
  const titleAndDescription = normalizeTag(`${source.title} ${source.description}`);
  const candidates = [
    `karmikborc${code}`,
    `karmicdebt${code}`,
    `karmik${code}`,
    `borc${code}`,
  ];
  return candidates.some((candidate) => normalizedTags.includes(candidate) || titleAndDescription.includes(candidate));
}

function matchesAnyKarmicCode(source: { title: string; description: string; tags?: string[] }, codes: number[]) {
  const normalizedTags = (source.tags ?? []).map(normalizeTag).filter(Boolean);
  const hasGeneralTag = normalizedTags.some((tag) => tag === "genel" || tag === "general" || tag.startsWith("genel") || tag.startsWith("general"));
  if (hasGeneralTag) return true;
  if (!codes.length) return true;
  return codes.some((code) => matchesKarmicCodeInSource(source, code));
}

function matchesKarmicCode(item: MeditationLibraryItem, code: number) {
  return matchesKarmicCodeInSource(item, code);
}

function isBreathItem(item: MeditationLibraryItem) {
  const normalizedTags = (item.tags ?? []).map(normalizeTag).filter(Boolean);
  const title = normalizeTag(item.title);
  const desc = normalizeTag(item.description);
  return normalizedTags.includes("nefes") || title.includes("nefes") || desc.includes("nefes");
}

async function fetchMeditationLibrary(): Promise<MeditationLibraryItem[]> {
  try {
    const res = await fetch("/api/numeroloji/meditations");
    const json = (await res.json().catch(() => null)) as any;
    if (!res.ok) return [];
    const data = json as MeditationLibraryResponse;
    return Array.isArray(data?.items) ? data.items : [];
  } catch {
    return [];
  }
}

async function fetchBreathLibrary(): Promise<MeditationLibraryItem[]> {
  try {
    const res = await fetch("/api/numeroloji/breaths");
    const json = (await res.json().catch(() => null)) as any;
    if (!res.ok) return [];
    const data = json as MeditationLibraryResponse;
    return Array.isArray(data?.items) ? data.items : [];
  } catch {
    return [];
  }
}

async function fetchDietLibrary(): Promise<DietLibraryItem[]> {
  try {
    const res = await fetch("/api/numeroloji/diets");
    const json = (await res.json().catch(() => null)) as any;
    if (!res.ok) return [];
    const data = json as DietLibraryResponse;
    return Array.isArray(data?.items) ? data.items.filter((item) => item.active !== false) : [];
  } catch {
    return [];
  }
}

async function fetchActivityLibrary(): Promise<ActivityLibraryItem[]> {
  try {
    const res = await fetch("/api/numeroloji/activities");
    const json = (await res.json().catch(() => null)) as any;
    if (!res.ok) return [];
    const data = json as ActivityLibraryResponse;
    return Array.isArray(data?.items) ? data.items.filter((item) => item.active !== false) : [];
  } catch {
    return [];
  }
}

const DIET_FOCUS_BY_CODE: Record<number, string> = {
  1: "protein ve kök sebze dengesi",
  2: "su tüketimi ve hafif-ılık öğünler",
  3: "renkli sebze ve meyve çeşitliliği",
  4: "düzenli öğün saati ve sade tabak planı",
  5: "esnek ama kontrollü porsiyon dengesi",
  6: "ev yapımı şefkatli öğün ritmi",
  7: "hafif akşam ve zihni sakinleştiren bitki çayları",
  8: "enerji veren dengeli kompleks karbonhidrat",
  9: "arınma odaklı sebze ağırlıklı tabak",
};

const TASK_PRESETS: Record<ActivityKey, Omit<CircleTaskDraft, "id" | "active">> = {
  meditasyon: {
    title: "Meditasyon Aktivasyonu",
    description: "Bugün seçilen meditasyonu yap ve deneyimini grupta paylaş.",
    kind: "meditasyon",
    points: 2,
    frequency: "daily",
    durationMinutes: 10,
  },
  nefes: {
    title: "Nefes Çalışması",
    description: "En az 5 dakika nefes çalışması uygula.",
    kind: "nefes",
    points: 1,
    frequency: "daily",
    durationMinutes: 5,
  },
  rituel: {
    title: "Ritüel Uygulaması",
    description: "Çember ritüelini uygula (niyet, yazma, şükran vb.).",
    kind: "rituel",
    points: 2,
    frequency: "weekly3",
    durationMinutes: 20,
  },
  sohbet: {
    title: "Paylaşım (Sohbet)",
    description: "Günün farkındalığını çember sohbetinde paylaş.",
    kind: "sohbet",
    points: 1,
    frequency: "weekly3",
    durationMinutes: 10,
  },
  diyet: {
    title: "Diyet Takibi",
    description: "Bugün için seçilen beslenme planını uygula ve kısa not düş.",
    kind: "diyet",
    points: 2,
    frequency: "daily",
    durationMinutes: 20,
  },
};

function makeDraftId(prefix: string) {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function buildActivitySuggestions(activity: ActivityKey, karmicCodes: number[], libraryItems?: MeditationLibraryItem[]): ActivitySuggestion[] {
  const uniqueCodes = Array.from(new Set(karmicCodes.filter((n) => Number.isFinite(n) && n >= 1 && n <= 9)));
  if (!uniqueCodes.length || activity === "sohbet") return [];

  const activeLibrary = (Array.isArray(libraryItems) ? libraryItems : []).filter((item) => item.active !== false);
  const primaryLibraryPool =
    activity === "nefes"
      ? activeLibrary.filter((item) => isBreathItem(item))
      : activity === "meditasyon"
        ? activeLibrary.filter((item) => !isBreathItem(item))
        : activeLibrary;
  const libraryPool = primaryLibraryPool.length ? primaryLibraryPool : activeLibrary;

  const perCode = uniqueCodes.map((code, index) => {
    const bundle = getShopBundleForCode(code);
    const meditationItem = bundle.items.find((item) => item.category === "Meditasyon");
    const ritualItem = bundle.items.find((item) => item.category === "Ritüel" || item.category === "Günlük çalışma");

    const matchFromLibrary =
      libraryPool.find((item) => matchesKarmicCode(item, code)) ??
      (libraryPool.length ? libraryPool[index % libraryPool.length] : null);

    if (activity === "meditasyon") {
      const title = matchFromLibrary?.title?.trim() || meditationItem?.title || `Karmik ${code} Meditasyonu`;
      const description =
        matchFromLibrary?.description?.trim() ||
        meditationItem?.description ||
        `${bundle.headline} temasını dengeleyecek 7 dakikalık meditasyon uygula.`;
      const audioUrl = toPublicUrl(matchFromLibrary?.audioUrl);
      const imageUrl = toPublicUrl((matchFromLibrary as any)?.coverImageUrl ?? (matchFromLibrary as any)?.imageUrl);
      const videoUrl = toPublicUrl((matchFromLibrary as any)?.videoUrl);
      const mergeKey = matchFromLibrary?.id ? `lib-${matchFromLibrary.id}` : `${activity}-${normalizeTag(title)}-${normalizeTag(description)}-${normalizeTag(audioUrl)}`;
      return {
        id: `${activity}-${code}-${matchFromLibrary?.id ?? "fallback"}`,
        mergeKey,
        suggestion: {
          id: `${activity}-${code}-${matchFromLibrary?.id ?? "fallback"}`,
          activity,
          karmicCode: code,
          karmicCodes: [code],
          title,
          description,
          kind: "meditasyon",
          points: 2,
          audioUrl,
          imageUrl,
          videoUrl,
          durationMinutes: Number(matchFromLibrary?.durationMinutes ?? 0) || undefined,
        } satisfies ActivitySuggestion,
      };
    }

    if (activity === "nefes") {
      const title = matchFromLibrary?.title?.trim() || `Karmik ${code} Nefes Akışı`;
      const description =
        matchFromLibrary?.description?.trim() ||
        `${bundle.headline} için 4-4-6 ritmiyle 8 tur nefes çalışması uygula.`;
      const audioUrl = toPublicUrl(matchFromLibrary?.audioUrl);
      const imageUrl = toPublicUrl((matchFromLibrary as any)?.coverImageUrl ?? (matchFromLibrary as any)?.imageUrl);
      const videoUrl = toPublicUrl((matchFromLibrary as any)?.videoUrl);
      const mergeKey = matchFromLibrary?.id ? `lib-${matchFromLibrary.id}` : `${activity}-${normalizeTag(title)}-${normalizeTag(description)}-${normalizeTag(audioUrl)}`;
      return {
        id: `${activity}-${code}-${matchFromLibrary?.id ?? "fallback"}`,
        mergeKey,
        suggestion: {
          id: `${activity}-${code}-${matchFromLibrary?.id ?? "fallback"}`,
          activity,
          karmicCode: code,
          karmicCodes: [code],
          title,
          description,
          kind: "nefes",
          points: 1,
          audioUrl,
          imageUrl,
          videoUrl,
          durationMinutes: Number(matchFromLibrary?.durationMinutes ?? 0) || undefined,
        } satisfies ActivitySuggestion,
      };
    }

    if (activity === "rituel") {
      return {
        id: `${activity}-${code}`,
        mergeKey: `${activity}-${code}`,
        suggestion: {
          id: `${activity}-${code}`,
          activity,
          karmicCode: code,
          karmicCodes: [code],
          title: ritualItem?.title ?? `Karmik ${code} Ritüeli`,
          description: ritualItem?.description ?? `${bundle.headline} başlığında kısa bir yazma/niyet ritüeli uygula.`,
          kind: "rituel",
          points: 2,
        } satisfies ActivitySuggestion,
      };
    }

    return {
      id: `${activity}-${code}`,
      mergeKey: `${activity}-${code}`,
      suggestion: {
        id: `${activity}-${code}`,
        activity,
        karmicCode: code,
        karmicCodes: [code],
        title: `Karmik ${code} Diyet Odağı`,
        description: `Bugün ${DIET_FOCUS_BY_CODE[code] ?? "denge"} temasına odaklan ve gün sonunda 1 cümle not al.`,
        kind: "diyet",
        points: 2,
      } satisfies ActivitySuggestion,
    };
  });

  const merged = new Map<string, ActivitySuggestion>();
  perCode.forEach((entry) => {
    const existing = merged.get(entry.mergeKey);
    if (!existing) {
      merged.set(entry.mergeKey, entry.suggestion);
      return;
    }
    const combinedCodes = Array.from(new Set([...(existing.karmicCodes ?? [existing.karmicCode]), ...entry.suggestion.karmicCodes])).sort((a, b) => a - b);
    merged.set(entry.mergeKey, {
      ...existing,
      karmicCode: combinedCodes[0] ?? existing.karmicCode,
      karmicCodes: combinedCodes,
    });
  });

  return Array.from(merged.values());
}

function buildTaskFromActivity(activity: ActivityKey, suggestion: ActivitySuggestion | null) {
  const preset = TASK_PRESETS[activity];
  if (suggestion) {
    const { title, description, kind, points } = suggestion;
    return {
      title,
      description,
      kind,
      points,
      frequency: preset.frequency,
      durationMinutes: preset.durationMinutes,
    };
  }
  return preset;
}

function buildDefaultTasksFromActivities(
  activities: ActivityKey[],
  selectedSuggestionByActivity: Partial<Record<ActivityKey, string>>,
  suggestionById: Map<string, ActivitySuggestion>,
  suggestionListByActivity: Partial<Record<ActivityKey, ActivitySuggestion[]>>,
) {
  return activities.map((activity) => {
    const selectedId = selectedSuggestionByActivity[activity];
    const selected = selectedId ? suggestionById.get(selectedId) ?? null : null;
    const fallback = suggestionListByActivity[activity]?.[0] ?? null;

    return {
      id: makeDraftId("task"),
      ...buildTaskFromActivity(activity, selected ?? fallback),
      active: true,
    } satisfies CircleTaskDraft;
  });
}

function parseSelectNumber(value: string) {
  const n = Math.trunc(Number(value));
  if (!Number.isFinite(n)) return null;
  if (n <= 0) return null;
  return n;
}

function StepItem(props: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  const { active, icon, label, onClick } = props;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-center transition-colors",
        active
          ? "border-[var(--panel-primary)] bg-[var(--panel-primary)]/10 text-[var(--panel-primary)]"
          : "border-slate-200 text-slate-600 hover:bg-slate-100 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-900",
      )}
    >
      <span className={cn("grid h-7 w-7 place-items-center rounded-lg", active ? "bg-white/70" : "bg-transparent")}>{icon}</span>
      <span className={cn("text-sm", active ? "font-bold" : "font-medium")}>{label}</span>
    </button>
  );
}

function ActivityCard(props: { active: boolean; title: string; description: string; icon: React.ReactNode; onClick: () => void }) {
  const { active, title, description, icon, onClick } = props;

  return (
    <button
      type="button"
      title={description}
      onClick={onClick}
      className={cn(
        "flex h-full w-full flex-col items-center gap-2 rounded-xl border-2 p-4 text-center transition-colors",
        active
          ? "border-[var(--panel-primary)] bg-[var(--panel-primary)]/5"
          : "border-slate-100 hover:border-[var(--panel-primary)]/30 dark:border-slate-800",
      )}
    >
      <div
        className={cn(
          "grid h-12 w-12 place-items-center rounded-full",
          active
            ? "bg-[var(--panel-primary)]/20 text-[var(--panel-primary)]"
            : "bg-slate-100 text-slate-500 dark:bg-slate-900 dark:text-slate-300",
        )}
      >
        {icon}
      </div>
      <div className="text-sm font-bold text-slate-900 dark:text-white">{title}</div>
      <div className="text-xs text-slate-500 dark:text-slate-400">{description}</div>
      <div
        className={cn(
          "text-[10px] font-bold uppercase tracking-wider",
          active ? "text-[var(--panel-primary)]" : "invisible",
        )}
      >
        Seçildi
      </div>
    </button>
  );
}

export default function UserPanelCemberYeni() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useSupabaseSession();
  const { toast } = useToast();

  const editCircleId = (searchParams.get("edit") ?? "").trim() || null;
  const isEditMode = Boolean(editCircleId);

  const [step, setStep] = useState<StepKey>("basic");
  const [createBusy, setCreateBusy] = useState(false);
  const [draftBusy, setDraftBusy] = useState(false);
  const [draftCircleId, setDraftCircleId] = useState<string | null>(null);
  const [shareLocationEnabled, setShareLocationEnabled] = useState(false);
  const [locationBusy, setLocationBusy] = useState(false);
  const [sharedLocation, setSharedLocation] = useState<{ lat: number; lng: number } | null>(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [circleType, setCircleType] = useState<CircleType>("Meditasyon Çemberi");
  const [circleDuration, setCircleDuration] = useState<CircleDurationOption>("21");
  const [participationRule, setParticipationRule] = useState<ParticipationRuleOption>("unlimited");

  const [energySystemEnabled, setEnergySystemEnabled] = useState(true);
  const [streakEnabled, setStreakEnabled] = useState(true);
  const [leaderboardEnabled, setLeaderboardEnabled] = useState(true);
  const [ritualHourEnabled, setRitualHourEnabled] = useState(true);
  const [ritualHour, setRitualHour] = useState<string>("21:00");
  const [auraEnabled, setAuraEnabled] = useState(true);
  const [auraType, setAuraType] = useState<CircleAura>("sifa");
  const [sharingAreaEnabled, setSharingAreaEnabled] = useState(true);
  const [dailyEnergyEnabled, setDailyEnergyEnabled] = useState(true);
  const [badgeSystemEnabled, setBadgeSystemEnabled] = useState(true);
  const [matchScoreEnabled, setMatchScoreEnabled] = useState(true);
  const [evolutionEnabled, setEvolutionEnabled] = useState(true);
  const [autoSuggestionEnabled, setAutoSuggestionEnabled] = useState(true);

  const [lifePathEnabled, setLifePathEnabled] = useState(true);
  const [lifePathNumber, setLifePathNumber] = useState<number | null>(null);

  const [heartEnabled, setHeartEnabled] = useState(false);
  const [heartNumber, setHeartNumber] = useState<number | null>(null);

  const [expressionEnabled, setExpressionEnabled] = useState(false);
  const [expressionNumber, setExpressionNumber] = useState<number | null>(null);

  const [personalityEnabled, setPersonalityEnabled] = useState(false);
  const [personalityNumber, setPersonalityNumber] = useState<number | null>(null);

  const [karmicDebtEnabled, setKarmicDebtEnabled] = useState(true);
  const [karmicDebtNumbers, setKarmicDebtNumbers] = useState<number[]>([]);

  const [activities, setActivities] = useState<ActivityKey[]>(["meditasyon"]);
  const [selectedSuggestionByActivity, setSelectedSuggestionByActivity] = useState<Partial<Record<ActivityKey, string>>>({});

  const draftHydratedRef = useRef(false);

  const latestDraftQuery = useQuery({
    queryKey: ["circle-latest-draft", user?.id],
    enabled: Boolean(user?.id) && !isEditMode,
    queryFn: () => getMyLatestCircleDraftInDb(),
    staleTime: 15_000,
    retry: 1,
  });

  const editCircleQuery = useQuery({
    queryKey: ["circle-edit-draft", user?.id, editCircleId],
    enabled: Boolean(user?.id) && isEditMode,
    queryFn: () => getMyCircleDraftByIdInDb(String(editCircleId ?? "")),
    staleTime: 15_000,
    retry: 1,
  });

  const meditationsQuery = useQuery({
    queryKey: ["panel-meditations"],
    queryFn: fetchMeditationLibrary,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const breathsQuery = useQuery({
    queryKey: ["panel-breaths"],
    queryFn: fetchBreathLibrary,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const dietsQuery = useQuery({
    queryKey: ["panel-diets"],
    queryFn: fetchDietLibrary,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const activitiesQuery = useQuery({
    queryKey: ["panel-activities"],
    queryFn: fetchActivityLibrary,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  function toggleActivity(key: ActivityKey) {
    setActivities((prev) => {
      const has = prev.includes(key);
      if (has) {
        const next = prev.filter((x) => x !== key);
        if (!next.length) return prev;
        setSelectedSuggestionByActivity((current) => {
          if (!(key in current)) return current;
          const copy = { ...current };
          delete copy[key];
          return copy;
        });
        return next;
      }
      return [...prev, key];
    });
  }

  const activityValue = useMemo(() => activities.join(","), [activities]);

  const activitySuggestionsByActivity = useMemo(() => {
    const selectedKarmicCodes = karmicDebtEnabled
      ? Array.from(new Set(karmicDebtNumbers.filter((code) => Number.isFinite(code) && code >= 1 && code <= 9)))
      : [];

    const mapProgramToSuggestion = (activity: ActivityKey, program: ProgramOption, index: number): ActivitySuggestion => {
      const preset = TASK_PRESETS[activity];
      return {
        id: `suggest-${activity}-${program.id}-${index}`,
        activity,
        karmicCode: selectedKarmicCodes[0] ?? 1,
        karmicCodes: selectedKarmicCodes,
        title: program.title,
        description: program.description,
        kind: preset.kind,
        points: preset.points,
        audioUrl: program.audioUrl,
        imageUrl: program.imageUrl,
        videoUrl: program.videoUrl,
        durationMinutes: program.durationMinutes,
      };
    };

    const meditationSuggestions = (meditationsQuery.data ?? [])
      .filter((item) => item.active !== false)
      .filter((item) => !isBreathItem(item))
      .filter((item) => matchesAnyKarmicCode(item, selectedKarmicCodes))
      .map((item, index) =>
        mapProgramToSuggestion(
          "meditasyon",
          {
            id: `med-${item.id}`,
            title: item.title,
            description: item.description,
            durationMinutes: item.durationMinutes,
            imageUrl: toPublicUrl((item as any).coverImageUrl ?? (item as any).imageUrl),
            audioUrl: toPublicUrl(item.audioUrl),
            videoUrl: toPublicUrl((item as any).videoUrl),
          },
          index,
        ),
      );

    const breathSuggestions = (breathsQuery.data ?? [])
      .filter((item) => item.active !== false)
      .filter((item) => matchesAnyKarmicCode(item, selectedKarmicCodes))
      .map((item, index) =>
        mapProgramToSuggestion(
          "nefes",
          {
            id: `bre-${item.id}`,
            title: item.title,
            description: item.description,
            durationMinutes: item.durationMinutes,
            imageUrl: toPublicUrl((item as any).coverImageUrl ?? (item as any).imageUrl),
            audioUrl: toPublicUrl(item.audioUrl),
            videoUrl: toPublicUrl((item as any).videoUrl),
          },
          index,
        ),
      );

    const ritualSuggestions = (activitiesQuery.data ?? [])
      .filter((item) => item.active !== false)
      .filter((item) => matchesAnyKarmicCode(item, selectedKarmicCodes))
      .map((item, index) =>
        mapProgramToSuggestion(
          "rituel",
          {
            id: `rit-${item.id}`,
            title: item.title,
            description: item.description,
            durationMinutes: item.durationMinutes,
            imageUrl: toPublicUrl((item as any).coverImageUrl ?? (item as any).imageUrl),
            audioUrl: toPublicUrl((item as any).audioUrl),
            videoUrl: toPublicUrl((item as any).videoUrl),
          },
          index,
        ),
      );

    const dietSuggestions = (dietsQuery.data ?? [])
      .filter((item) => item.active !== false)
      .filter((item) =>
        matchesAnyKarmicCode(
          {
            title: item.title,
            description: item.description,
            tags: item.tags ?? [],
          },
          selectedKarmicCodes,
        ),
      )
      .map((item, index) =>
        mapProgramToSuggestion(
          "diyet",
          {
            id: `diet-${item.id}`,
            title: item.title,
            description: item.description,
            durationMinutes: 20,
            imageUrl: toPublicUrl(item.coverImageUrl),
            audioUrl: toPublicUrl((item as any).audioUrl),
            videoUrl: toPublicUrl((item as any).videoUrl),
          },
          index,
        ),
      );

    return {
      meditasyon: meditationSuggestions,
      nefes: breathSuggestions,
      rituel: ritualSuggestions,
      diyet: dietSuggestions,
      sohbet: [],
    } satisfies Partial<Record<ActivityKey, ActivitySuggestion[]>>;
  }, [activitiesQuery.data, breathsQuery.data, dietsQuery.data, karmicDebtEnabled, karmicDebtNumbers, meditationsQuery.data]);

  const activitySuggestionById = useMemo(() => {
    const entries = Object.values(activitySuggestionsByActivity).flatMap((list) => list ?? []);
    return new Map(entries.map((entry) => [entry.id, entry]));
  }, [activitySuggestionsByActivity]);

  const [tasks, setTasks] = useState<CircleTaskDraft[]>(() =>
    buildDefaultTasksFromActivities(["meditasyon"], {}, new Map<string, ActivitySuggestion>(), {}),
  );
  const [programSearchByActivity, setProgramSearchByActivity] = useState<Partial<Record<ActivityKey, string>>>({});
  const [programVisibleCountByActivity, setProgramVisibleCountByActivity] = useState<Partial<Record<ActivityKey, number>>>({});
  const [suggestionSearchByActivity, setSuggestionSearchByActivity] = useState<Partial<Record<ActivityKey, string>>>({});

  const programOptionsByActivity = useMemo(() => {
    const selectedKarmicCodes = karmicDebtEnabled
      ? Array.from(new Set(karmicDebtNumbers.filter((code) => Number.isFinite(code) && code >= 1 && code <= 9)))
      : [];

    const meditationPrograms = (meditationsQuery.data ?? [])
      .filter((item) => item.active !== false)
      .filter((item) => matchesAnyKarmicCode(item, selectedKarmicCodes))
      .map((item) => ({
        id: `med-${item.id}`,
        title: item.title,
        description: item.description,
        durationMinutes: item.durationMinutes,
        imageUrl: toPublicUrl((item as any).coverImageUrl ?? (item as any).imageUrl),
        audioUrl: toPublicUrl(item.audioUrl),
        videoUrl: toPublicUrl((item as any).videoUrl),
      } satisfies ProgramOption));

    const breathPrograms = (breathsQuery.data ?? [])
      .filter((item) => item.active !== false)
      .filter((item) => matchesAnyKarmicCode(item, selectedKarmicCodes))
      .map((item) => ({
        id: `bre-${item.id}`,
        title: item.title,
        description: item.description,
        durationMinutes: item.durationMinutes,
        imageUrl: toPublicUrl((item as any).coverImageUrl ?? (item as any).imageUrl),
        audioUrl: toPublicUrl(item.audioUrl),
        videoUrl: toPublicUrl((item as any).videoUrl),
      } satisfies ProgramOption));

    const ritualPrograms = (activitiesQuery.data ?? [])
      .filter((item) => matchesAnyKarmicCode(item, selectedKarmicCodes))
      .map((item) => ({
        id: `rit-${item.id}`,
        title: item.title,
        description: item.description,
        durationMinutes: item.durationMinutes,
        imageUrl: toPublicUrl((item as any).coverImageUrl ?? (item as any).imageUrl),
        audioUrl: toPublicUrl((item as any).audioUrl),
        videoUrl: toPublicUrl((item as any).videoUrl),
      } satisfies ProgramOption));

    const dietPrograms = (dietsQuery.data ?? [])
      .filter((item) =>
        matchesAnyKarmicCode(
          {
            title: item.title,
            description: item.description,
            tags: item.tags ?? [],
          },
          selectedKarmicCodes,
        ),
      )
      .map((item) => ({
        id: `diet-${item.id}`,
        title: item.title,
        description: item.description,
        durationMinutes: 20,
        imageUrl: toPublicUrl(item.coverImageUrl),
        audioUrl: toPublicUrl((item as any).audioUrl),
        videoUrl: toPublicUrl((item as any).videoUrl),
      } satisfies ProgramOption));

    return {
      meditasyon: meditationPrograms,
      nefes: breathPrograms,
      rituel: ritualPrograms,
      diyet: dietPrograms,
      sohbet: [],
    } as Record<ActivityKey, ProgramOption[]>;
  }, [activitiesQuery.data, breathsQuery.data, dietsQuery.data, karmicDebtEnabled, karmicDebtNumbers, meditationsQuery.data]);

  const rules = useMemo(() => {
    const all: KarmaCircleRule[] = [
      lifePathEnabled && lifePathNumber
        ? ({ key: "destinyNumber", value: lifePathNumber } satisfies KarmaCircleRule)
        : null,
      heartEnabled && heartNumber
        ? ({ key: "heartDesireNumber", value: heartNumber } satisfies KarmaCircleRule)
        : null,
      expressionEnabled && expressionNumber
        ? ({ key: "expressionNumber", value: expressionNumber } satisfies KarmaCircleRule)
        : null,
      personalityEnabled && personalityNumber
        ? ({ key: "personalityNumber", value: personalityNumber } satisfies KarmaCircleRule)
        : null,
      ...(karmicDebtEnabled ? karmicDebtNumbers.map((n) => ({ key: "karmicDebt", value: n } satisfies KarmaCircleRule)) : []),
    ].filter(Boolean) as KarmaCircleRule[];

    const ruleset: KarmaCircleRuleSet = { all };
    return ruleset;
  }, [
    expressionEnabled,
    expressionNumber,
    heartEnabled,
    heartNumber,
    karmicDebtEnabled,
    karmicDebtNumbers,
    lifePathEnabled,
    lifePathNumber,
    personalityEnabled,
    personalityNumber,
  ]);

  const stepIndex = Math.max(0, STEP_ORDER.indexOf(step)) + 1;
  const progressPercent = Math.round((stepIndex / STEP_ORDER.length) * 100);
  const estimatedEnergy = Math.max(10, Math.min(100, tasks.filter((t) => t.active).length * 15 + rules.all.length * 10));
  const activeTasksCount = tasks.filter((t) => t.active && t.title.trim()).length;
  const ritualHourSummary = ritualHourEnabled ? ritualHour : "Kapalı";
  const auraConfig = CIRCLE_AURA_OPTIONS.find((option) => option.value === auraType) ?? CIRCLE_AURA_OPTIONS[0];
  const circleEvolutionLevel = estimatedEnergy >= 80 ? "Seviye 3 – Güç Alanı" : estimatedEnergy >= 55 ? "Seviye 2 – Aktif Çember" : "Seviye 1 – Yeni Çember";
  const sampleUserStreak = Math.max(1, Math.min(21, activeTasksCount * 2));
  const sampleMatchScore = Math.max(50, Math.min(98, rules.all.length * 12 + activeTasksCount * 8));
  const participationRuleLabel = PARTICIPATION_RULE_OPTIONS.find((option) => option.value === participationRule)?.label ?? "Sınırsız";

  const matchEstimateQuery = useQuery({
    queryKey: ["circle-match-estimate", rules],
    queryFn: () => estimateCircleMatchCount(rules),
    enabled: rules.all.length > 0,
    staleTime: 60_000,
    retry: 1,
  });

  const canCreate = name.trim().length >= 2 && rules.all.length > 0 && activities.length > 0 && tasks.some((t) => t.active && t.title.trim());

  const onShareLocation = () => {
    if (!navigator.geolocation) {
      toast({ title: "Konum desteklenmiyor", description: "Tarayıcın konum paylaşımını desteklemiyor." });
      return;
    }

    setLocationBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setSharedLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setShareLocationEnabled(true);
        setLocationBusy(false);
        toast({ title: "Konum alındı", description: "Çemberle birlikte haritada ritüel pini oluşturulacak." });
      },
      () => {
        setLocationBusy(false);
        toast({
          title: "Konum alınamadı",
          description: "Konum izni verirsen çember ritüeli haritada görünebilir.",
          variant: "destructive",
        });
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const activeRulesLabel = useMemo(() => {
    const labels: string[] = [];
    if (lifePathEnabled && lifePathNumber) labels.push(`Yaşam Yolu: ${lifePathNumber}`);
    if (heartEnabled && heartNumber) labels.push(`Kalp Arzusu: ${heartNumber}`);
    if (expressionEnabled && expressionNumber) labels.push(`İfade: ${expressionNumber}`);
    if (personalityEnabled && personalityNumber) labels.push(`Kişilik: ${personalityNumber}`);
    if (karmicDebtEnabled) {
      if (karmicDebtNumbers.length) labels.push(`Karmik Borç: ${karmicDebtNumbers.join(", ")}`);
      else labels.push("Karmik Borç: Açık");
    }
    labels.push(`Çember Türü: ${circleType}`);
    labels.push(`Çember Süresi: ${circleDuration === "unlimited" ? "Süresiz" : `${circleDuration} gün`}`);
    labels.push(`Katılım Kuralı: ${participationRuleLabel}`);
    labels.push(`Aktivite: ${activities.map((a) => ACTIVITY_LABELS[a]).join(", ")}`);
    labels.push(`Görevler: ${tasks.filter((t) => t.active && t.title.trim()).length}`);
    labels.push(`Ritüel Saati: ${ritualHourSummary}`);
    labels.push(`Aura: ${auraEnabled ? `${auraConfig.label} (${auraConfig.hz} Hz)` : "Kapalı"}`);
    labels.push(`Mekanikler: ${[
      energySystemEnabled ? "Enerji" : null,
      streakEnabled ? "Streak" : null,
      leaderboardEnabled ? "Lider Tablosu" : null,
      sharingAreaEnabled ? "Paylaşım" : null,
      dailyEnergyEnabled ? "Günün Enerjisi" : null,
      badgeSystemEnabled ? "Rozet" : null,
      matchScoreEnabled ? "Eşleşme Skoru" : null,
      evolutionEnabled ? "Evrim" : null,
      autoSuggestionEnabled ? "Otomatik Öneri" : null,
    ].filter(Boolean).join(", ") || "Seçilmedi"}`);
    for (const t of tasks.filter((t) => t.active && t.title.trim()).slice(0, 8)) {
      labels.push(`Görev: ${t.title} (${TASK_FREQUENCY_LABELS[t.frequency]} • ${t.durationMinutes} dk)`);
    }
    return labels;
  }, [
    activities,
    tasks,
    expressionEnabled,
    expressionNumber,
    heartEnabled,
    heartNumber,
    karmicDebtEnabled,
    karmicDebtNumbers,
    lifePathEnabled,
    lifePathNumber,
    personalityEnabled,
    personalityNumber,
    circleDuration,
    circleType,
    ritualHourSummary,
    auraEnabled,
    auraConfig.label,
    auraConfig.hz,
    energySystemEnabled,
    streakEnabled,
    leaderboardEnabled,
    sharingAreaEnabled,
    dailyEnergyEnabled,
    badgeSystemEnabled,
    matchScoreEnabled,
    evolutionEnabled,
    autoSuggestionEnabled,
    participationRuleLabel,
  ]);

  const draftSource = isEditMode ? editCircleQuery.data : latestDraftQuery.data;

  useEffect(() => {
    draftHydratedRef.current = false;
  }, [editCircleId]);

  useEffect(() => {
    if (draftHydratedRef.current) return;
    if (!draftSource) return;

    const draft = draftSource;
    draftHydratedRef.current = true;
    setDraftCircleId(draft.id);

    setName(draft.name || "");
    setDescription(draft.description || "");
    setImageUrl(draft.imageUrl || "");

    const [draftTypePart, draftActivitiesPart] = String(draft.activity || "")
      .split("|")
      .map((value) => value.trim());

    if (draftTypePart && CIRCLE_TYPES.includes(draftTypePart as CircleType)) {
      setCircleType(draftTypePart as CircleType);
    } else if (draft.circleType && CIRCLE_TYPES.includes(draft.circleType as CircleType)) {
      setCircleType(draft.circleType as CircleType);
    }

    const restoredActivities = (draftActivitiesPart ?? draft.activity)
      .split(",")
      .map((value) => value.trim())
      .filter((value): value is ActivityKey => ["meditasyon", "nefes", "rituel", "sohbet", "diyet"].includes(value));
    if (restoredActivities.length) setActivities(Array.from(new Set(restoredActivities)));

    if (Number.isFinite(Number(draft.durationDays))) {
      const durationText = String(Math.trunc(Number(draft.durationDays))) as CircleDurationOption;
      if (durationText === "7" || durationText === "21" || durationText === "40") setCircleDuration(durationText);
    } else {
      setCircleDuration("unlimited");
    }

    const social = draft.socialFeatures ?? {};
    if (typeof social.energySystemEnabled === "boolean") setEnergySystemEnabled(Boolean(social.energySystemEnabled));
    if (typeof social.streakEnabled === "boolean") setStreakEnabled(Boolean(social.streakEnabled));
    if (typeof social.leaderboardEnabled === "boolean") setLeaderboardEnabled(Boolean(social.leaderboardEnabled));
    if (typeof social.ritualHour === "string" && social.ritualHour) {
      setRitualHourEnabled(true);
      setRitualHour(social.ritualHour);
    }
    if (social.ritualHour === null) setRitualHourEnabled(false);
    if (social.aura && typeof social.aura === "object") {
      const auraTypeValue = String((social.aura as any).type || "");
      if (auraTypeValue === "sifa" || auraTypeValue === "bolluk" || auraTypeValue === "denge") {
        setAuraType(auraTypeValue as CircleAura);
      }
      setAuraEnabled(true);
    }
    if (social.aura === null) setAuraEnabled(false);
    if (typeof social.sharingAreaEnabled === "boolean") setSharingAreaEnabled(Boolean(social.sharingAreaEnabled));
    if (typeof social.dailyEnergyEnabled === "boolean") setDailyEnergyEnabled(Boolean(social.dailyEnergyEnabled));
    if (typeof social.badgeSystemEnabled === "boolean") setBadgeSystemEnabled(Boolean(social.badgeSystemEnabled));
    if (typeof social.matchScoreEnabled === "boolean") setMatchScoreEnabled(Boolean(social.matchScoreEnabled));
    if (typeof social.evolutionEnabled === "boolean") setEvolutionEnabled(Boolean(social.evolutionEnabled));
    if (typeof social.autoSuggestionEnabled === "boolean") setAutoSuggestionEnabled(Boolean(social.autoSuggestionEnabled));
    if (social.participationRule === "7" || social.participationRule === "14" || social.participationRule === "21" || social.participationRule === "unlimited") {
      setParticipationRule(social.participationRule as ParticipationRuleOption);
    }

    const restoredRules = draft.rules?.all ?? [];
    const findRule = (key: KarmaCircleRule["key"]) => restoredRules.find((rule) => rule.key === key)?.value ?? null;
    const karmic = restoredRules.filter((rule) => rule.key === "karmicDebt").map((rule) => rule.value).filter((n) => Number.isFinite(n));

    setLifePathEnabled(Boolean(findRule("destinyNumber")));
    setLifePathNumber(findRule("destinyNumber"));
    setHeartEnabled(Boolean(findRule("heartDesireNumber")));
    setHeartNumber(findRule("heartDesireNumber"));
    setExpressionEnabled(Boolean(findRule("expressionNumber")));
    setExpressionNumber(findRule("expressionNumber"));
    setPersonalityEnabled(Boolean(findRule("personalityNumber")));
    setPersonalityNumber(findRule("personalityNumber"));
    setKarmicDebtEnabled(karmic.length > 0);
    setKarmicDebtNumbers(karmic.map((n) => Math.trunc(Number(n))).filter((n) => n >= 1 && n <= 9));

    const restoredTasks = Array.isArray(draft.tasks)
      ? draft.tasks
          .map((task) => {
            const frequency = String((task as any).frequency ?? "daily");
            const normalizedFrequency: TaskFrequency = frequency === "weekly3" || frequency === "weekly1" ? (frequency as TaskFrequency) : "daily";
            const duration = Number((task as any).durationMinutes ?? 10);
            const durationMinutes = Number.isFinite(duration) && duration > 0 ? Math.trunc(duration) : 10;
            const pointsRaw = Number((task as any).points ?? 1);
            return {
              id: String((task as any).id ?? makeDraftId("task")),
              title: String((task as any).title ?? ""),
              description: String((task as any).description ?? ""),
              imageUrl: typeof (task as any).imageUrl === "string" ? String((task as any).imageUrl) : "",
              kind: ((task as any).kind ?? "ozel") as CircleTaskKind,
              points: Number.isFinite(pointsRaw) ? Math.max(0, Math.min(100, Math.trunc(pointsRaw))) : 1,
              frequency: normalizedFrequency,
              durationMinutes,
              active: (task as any).active !== false,
            } satisfies CircleTaskDraft;
          })
          .filter((task) => task.title.trim() || task.description.trim())
      : [];

    if (restoredTasks.length) setTasks(restoredTasks);
  }, [draftSource]);

  const onSaveDraft = async () => {
    if (!user?.id || draftBusy) return;
    setDraftBusy(true);
    try {
      const saved = await saveCircleDraftInDb({
        draftId: draftCircleId,
        name,
        description,
        activity: `${circleType} | ${activityValue}`,
        activities,
        imageUrl,
        circleType,
        durationDays: circleDuration === "unlimited" ? null : Number(circleDuration),
        maxParticipants: null,
        showOnRitualMap: shareLocationEnabled,
        rules,
        tasks,
        socialFeatures: {
          energySystemEnabled,
          streakEnabled,
          leaderboardEnabled,
          ritualHour: ritualHourEnabled ? ritualHour : null,
          aura: auraEnabled ? { type: auraType, label: auraConfig.label, hz: auraConfig.hz } : null,
          sharingAreaEnabled,
          dailyEnergyEnabled,
          badgeSystemEnabled,
          matchScoreEnabled,
          evolutionEnabled,
          autoSuggestionEnabled,
          participationRule,
        },
      });
      setDraftCircleId(saved.id);
      toast({ title: "Taslak kaydedildi", description: "Kaldığın yerden devam edebilirsin." });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Taslak kaydedilemedi";
      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
    } finally {
      setDraftBusy(false);
    }
  };

  async function readFileAsDataUrl(file: File) {
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Görsel okunamadı"));
      reader.onload = () => resolve(String(reader.result ?? ""));
      reader.readAsDataURL(file);
    });
  }

  function appendTaskFromProgram(activity: ActivityKey, program: ProgramOption) {
    const preset = TASK_PRESETS[activity];
    setTasks((prev) => [
      ...prev,
      {
        id: makeDraftId("task"),
        title: program.title,
        description: program.description,
        imageUrl: program.imageUrl ?? "",
        kind: preset.kind,
        points: preset.points,
        frequency: preset.frequency,
        durationMinutes: Number.isFinite(Number(program.durationMinutes)) && Number(program.durationMinutes) > 0 ? Math.trunc(Number(program.durationMinutes)) : preset.durationMinutes,
        active: true,
      },
    ]);
  }

  function appendTaskFromSuggestion(suggestion: ActivitySuggestion) {
    const preset = TASK_PRESETS[suggestion.activity];
    setSelectedSuggestionByActivity((prev) => ({ ...prev, [suggestion.activity]: suggestion.id }));
    setTasks((prev) => [
      ...prev,
      {
        id: makeDraftId("task"),
        title: suggestion.title,
        description: suggestion.description,
        imageUrl: suggestion.imageUrl ?? "",
        kind: suggestion.kind ?? preset.kind,
        points: suggestion.points ?? preset.points,
        frequency: preset.frequency,
        durationMinutes:
          Number.isFinite(Number(suggestion.durationMinutes)) && Number(suggestion.durationMinutes) > 0
            ? Math.trunc(Number(suggestion.durationMinutes))
            : preset.durationMinutes,
        active: true,
      },
    ]);
  }

  return (
    <div className="mx-auto w-full max-w-7xl">
      <div className="mb-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{isEditMode ? "Çemberi Düzenle" : "Yeni Çember Oluştur"}</div>
            <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">Numerolojik uyumu yakalayın</div>
            {draftCircleId ? <div className="mt-1 text-xs font-semibold text-[var(--panel-primary)]">{isEditMode ? "Çember verisi yüklendi: düzenleyip kaydedebilirsin." : "Taslak yüklendi: kaldığın yerden devam ediyorsun."}</div> : null}
          </div>

          <div className="flex items-center gap-2">
            <Button type="button" variant="secondary" className="rounded-xl" onClick={() => void onSaveDraft()} disabled={draftBusy || !user?.id}>
              {draftBusy ? "Kaydediliyor..." : isEditMode ? "Düzenlemeyi Kaydet" : draftCircleId ? "Taslağı Güncelle" : "Taslak Kaydet"}
            </Button>
            <Button asChild variant="outline" className="rounded-xl">
              <Link to="/panel/cember">
                <ArrowLeft className="mr-2 h-4 w-4" /> Çemberlere dön
              </Link>
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 dark:border-slate-800 dark:bg-slate-950/40">
          <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <span>Adım {stepIndex}/{STEP_ORDER.length}</span>
            <span>{STEP_LABELS[step]}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
            <div className="h-full rounded-full bg-[var(--panel-primary)] transition-all" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>
      </div>

      <div className="mb-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        <StepItem
          active={step === "basic"}
          icon={<Info className="h-5 w-5" />}
          label="Temel Bilgiler"
          onClick={() => setStep("basic")}
        />
        <StepItem
          active={step === "filters"}
          icon={<Fingerprint className="h-5 w-5" />}
          label="Numerolojik Filtreler"
          onClick={() => setStep("filters")}
        />
        <StepItem
          active={step === "activity"}
          icon={<Wand2 className="h-5 w-5" />}
          label="Aktivite Seçimi"
          onClick={() => setStep("activity")}
        />
        <StepItem
          active={step === "tasks"}
          icon={<ListChecks className="h-5 w-5" />}
          label="Görevler"
          onClick={() => setStep("tasks")}
        />
        <StepItem
          active={step === "preview"}
          icon={<Eye className="h-5 w-5" />}
          label="Önizleme"
          onClick={() => setStep("preview")}
        />
      </div>

      <div className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[var(--panel-primary)]/25 to-sky-300 p-6 text-slate-900 shadow-xl lg:col-span-2">
              <div className="relative z-10">
                <div className="mb-6 flex items-start justify-between">
                  <div className="rounded bg-white/60 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-900">Canlı Önizleme</div>
                  <Eye className="h-5 w-5" />
                </div>
                <div className="mb-1 text-2xl font-black">{name.trim() || "Yeni Çember"}</div>
                <div className="mb-3 text-sm font-medium text-slate-700">{circleType}</div>
                <div className="flex flex-wrap gap-2 text-xs font-semibold text-slate-700">
                  <span className="rounded-full bg-white/60 px-2 py-1">{circleDuration === "unlimited" ? "Süresiz" : `${circleDuration} gün`}</span>
                  <span className="rounded-full bg-white/60 px-2 py-1">Katılım: {participationRuleLabel}</span>
                  <span className="rounded-full bg-white/60 px-2 py-1">{rules.all.length} filtre</span>
                </div>
                <div className="mt-3 text-sm text-slate-700">
                  {activities.length} aktivite • {tasks.filter((t) => t.active && t.title.trim()).length} görev
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-700">Çember Enerji Seviyesi: %{estimatedEnergy}</div>
                <div className="mt-1 text-xs font-medium text-slate-700">
                  {ritualHourEnabled ? `Bugünkü Ritüel: ${ritualHour}` : "Ritüel saati kapalı"} • {auraEnabled ? `${auraConfig.label} (${auraConfig.hz} Hz)` : "Aura kapalı"}
                </div>
              </div>
            </div>

            <div className="h-full rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
              <div className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Çember Kartı Önizleme</div>
              <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="h-28 bg-slate-200 dark:bg-slate-800">
                  {imageUrl.trim() ? <img src={imageUrl} alt="Çember kartı" className="h-full w-full object-cover" /> : null}
                </div>
                <div className="space-y-1 p-3">
                  <div className="text-sm font-bold text-slate-900 dark:text-white">{name.trim() || "Yeni Çember"}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">{circleType}</div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">
                    {circleDuration === "unlimited" ? "Süresiz" : `${circleDuration} gün`} • Katılım: {participationRuleLabel}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <main className="rounded-2xl border border-slate-200 bg-white p-8 text-slate-900 shadow-sm dark:border-slate-800 dark:bg-slate-950 dark:text-slate-900">
          {step === "basic" ? (
            <div className="space-y-6">
              <div className="flex items-center gap-2 border-b border-slate-100 pb-3 dark:border-slate-800">
                <div className="grid h-6 w-6 place-items-center rounded-full bg-[var(--panel-primary)] text-xs font-bold text-white">1</div>
                <div className="text-lg font-bold">Temel Bilgiler</div>
              </div>

              <div className="grid gap-6">
                <div className="space-y-2">
                  <Label>Çember Adı</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn: Bolluk ve Bereket Çemberi" />
                </div>

                <div className="space-y-2">
                  <Label>Açıklama</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Çemberin amacını ve ruhunu tarif edin..."
                    rows={4}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Çember Türü</Label>
                  <Select value={circleType} onValueChange={(v) => setCircleType(v as CircleType)}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Çember türü seç" />
                    </SelectTrigger>
                    <SelectContent>
                      {CIRCLE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Çember Süresi</Label>
                    <Select value={circleDuration} onValueChange={(v) => setCircleDuration(v as CircleDurationOption)}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder="Süre seç" />
                      </SelectTrigger>
                      <SelectContent>
                        {CIRCLE_DURATION_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Katılım Kuralı</Label>
                    <Select value={participationRule} onValueChange={(v) => setParticipationRule(v as ParticipationRuleOption)}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder="Katılım kuralı seç" />
                      </SelectTrigger>
                      <SelectContent>
                        {PARTICIPATION_RULE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                    <ImagePlus className="h-4 w-4 text-[var(--panel-primary)]" />
                    Çember Görseli (Opsiyonel)
                  </div>

                  <div className="rounded-2xl border border-dashed border-[var(--panel-primary)]/20 bg-slate-50/60 p-6 text-center dark:border-[var(--panel-primary)]/25 dark:bg-slate-900/30">
                    <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-full bg-[var(--panel-primary)]/10 text-[var(--panel-primary)]">
                      <ImagePlus className="h-7 w-7" />
                    </div>
                    <div className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Kapak Fotoğrafı Seçin</div>
                    <div className="mx-auto mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
                      Ruhsal yolculuğunuzu en iyi ifade eden görseli sürükleyin veya seçin.
                    </div>

                    <div className="mx-auto mt-5 grid max-w-lg gap-3 sm:grid-cols-[1fr_auto]">
                      <Input
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        placeholder="Görsel linki yapıştır..."
                        className="h-11 rounded-xl bg-white dark:bg-slate-950/50"
                      />

                      <label className="inline-flex h-11 cursor-pointer items-center justify-center rounded-xl bg-[var(--panel-primary)] px-5 text-sm font-semibold text-white shadow-[0_10px_24px_-12px_var(--panel-primary)] transition hover:opacity-95">
                        Gözat
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.currentTarget.files?.[0] ?? null;
                            e.currentTarget.value = "";
                            if (!file) return;

                            void readFileAsDataUrl(file)
                              .then((dataUrl) => setImageUrl(dataUrl))
                              .catch((err) => {
                                const msg = err instanceof Error ? err.message : "Görsel yüklenemedi";
                                toast({ title: "Görsel yüklenemedi", description: msg, variant: "destructive" });
                              });
                          }}
                        />
                      </label>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-950/30">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Kart Önizleme</div>
                    <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
                      <div className="h-24 bg-slate-200 dark:bg-slate-800">
                        {imageUrl.trim() ? <img src={imageUrl} alt="Çember önizleme" className="h-full w-full object-cover" /> : null}
                      </div>
                      <div className="p-2 text-xs text-slate-600 dark:text-slate-300">
                        <div className="font-bold text-slate-900 dark:text-white">{name.trim() || "Yeni Çember"}</div>
                        <div>{circleType}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button className="rounded-xl" type="button" onClick={() => setStep("filters")}>
                    Filtrelere Geç
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          {step === "filters" ? (
            <div className="space-y-6">
              <div className="flex items-center gap-2 border-b border-slate-100 pb-3 dark:border-slate-800">
                <div className="grid h-6 w-6 place-items-center rounded-full bg-[var(--panel-primary)] text-xs font-bold text-white">2</div>
                <div className="text-lg font-bold">Numerolojik Filtreler</div>
              </div>

              <div className="text-sm text-slate-500 dark:text-slate-400">
                Bu filtreler çembere katılabilecek kişilerin numerolojik uyumunu belirler.
                <span className="block font-semibold text-slate-700 dark:text-slate-200">
                  En az 1 filtre seçmelisin.
                </span>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/30">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Filtre Uyum Durumu</div>
                {matchEstimateQuery.isLoading ? (
                  <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">Eşleşen kişi sayısı hesaplanıyor...</div>
                ) : matchEstimateQuery.data ? (
                  <div className="mt-1 space-y-1">
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                      Bu filtrelerle eşleşen kişi sayısı: {matchEstimateQuery.data.count}
                    </div>
                    {matchEstimateQuery.data.count === 0 ? (
                      <div className="text-xs text-rose-600 dark:text-rose-400">Filtreler çok dar olabilir. Daha geniş bir kombinasyon deneyin.</div>
                    ) : matchEstimateQuery.data.count <= 3 ? (
                      <div className="text-xs text-amber-600 dark:text-amber-400">Eşleşme az ama odaklı bir grup için güçlü olabilir.</div>
                    ) : null}
                  </div>
                ) : matchEstimateQuery.isError ? (
                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Eşleşme sayısı şu an hesaplanamadı.</div>
                ) : (
                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Eşleşme sayısını görmek için filtre seç.</div>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <Card className="rounded-xl border-slate-100 bg-slate-50 p-4 text-slate-900 dark:border-slate-800 dark:bg-slate-900/30 dark:text-slate-900">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-bold text-slate-900">Hayat Yolu (Life Path)</div>
                    <Checkbox checked={lifePathEnabled} onCheckedChange={(v) => setLifePathEnabled(Boolean(v))} />
                  </div>

                  <Select
                    value={lifePathNumber ? String(lifePathNumber) : "_"}
                    onValueChange={(v) => setLifePathNumber(v === "_" ? null : parseSelectNumber(v))}
                    disabled={!lifePathEnabled}
                  >
                    <SelectTrigger className={cn("text-slate-900 placeholder:text-slate-500", !lifePathEnabled ? "opacity-50" : "")}> 
                      <SelectValue placeholder="Herkes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_">Herkes</SelectItem>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          Sadece {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Card>

                <Card className="rounded-xl border-slate-100 bg-slate-50 p-4 text-slate-900 dark:border-slate-800 dark:bg-slate-900/30 dark:text-slate-900">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-bold text-slate-900">Kalp Arzusu</div>
                    <Checkbox checked={heartEnabled} onCheckedChange={(v) => setHeartEnabled(Boolean(v))} />
                  </div>

                  <Select
                    value={heartNumber ? String(heartNumber) : "_"}
                    onValueChange={(v) => setHeartNumber(v === "_" ? null : parseSelectNumber(v))}
                    disabled={!heartEnabled}
                  >
                    <SelectTrigger className={cn("text-slate-900 placeholder:text-slate-500", !heartEnabled ? "opacity-50" : "")}> 
                      <SelectValue placeholder="Seçim Yapın" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_">Seçim Yapın</SelectItem>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Card>

                <Card className="rounded-xl border-slate-100 bg-slate-50 p-4 text-slate-900 dark:border-slate-800 dark:bg-slate-900/30 dark:text-slate-900">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-bold text-slate-900">İfade Numarası</div>
                    <Checkbox checked={expressionEnabled} onCheckedChange={(v) => setExpressionEnabled(Boolean(v))} />
                  </div>

                  <Select
                    value={expressionNumber ? String(expressionNumber) : "_"}
                    onValueChange={(v) => setExpressionNumber(v === "_" ? null : parseSelectNumber(v))}
                    disabled={!expressionEnabled}
                  >
                    <SelectTrigger className={cn("text-slate-900 placeholder:text-slate-500", !expressionEnabled ? "opacity-50" : "")}>
                      <SelectValue placeholder="Seçim Yapın" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_">Seçim Yapın</SelectItem>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Card>

                <Card className="rounded-xl border-slate-100 bg-slate-50 p-4 text-slate-900 dark:border-slate-800 dark:bg-slate-900/30 dark:text-slate-900">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-bold text-slate-900">Kişilik Sayısı</div>
                    <Checkbox checked={personalityEnabled} onCheckedChange={(v) => setPersonalityEnabled(Boolean(v))} />
                  </div>

                  <Select
                    value={personalityNumber ? String(personalityNumber) : "_"}
                    onValueChange={(v) => setPersonalityNumber(v === "_" ? null : parseSelectNumber(v))}
                    disabled={!personalityEnabled}
                  >
                    <SelectTrigger className={cn("text-slate-900 placeholder:text-slate-500", !personalityEnabled ? "opacity-50" : "")}> 
                      <SelectValue placeholder="Seçim Yapın" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_">Seçim Yapın</SelectItem>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Card>

                <Card className="rounded-xl border-slate-100 bg-slate-50 p-4 text-slate-900 md:col-span-2 dark:border-slate-800 dark:bg-slate-900/30 dark:text-slate-900">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-bold text-slate-900">Karmik Borç</div>
                    <Checkbox checked={karmicDebtEnabled} onCheckedChange={(v) => setKarmicDebtEnabled(Boolean(v))} />
                  </div>

                  <div className={cn("grid grid-cols-2 gap-3", !karmicDebtEnabled ? "opacity-50" : "")}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => {
                      const checked = karmicDebtNumbers.includes(n);
                      return (
                        <label key={n} className="flex items-center gap-2 text-sm text-slate-900">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={checked}
                            disabled={!karmicDebtEnabled}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? Array.from(new Set([...karmicDebtNumbers, n]))
                                : karmicDebtNumbers.filter((x) => x !== n);
                              setKarmicDebtNumbers(next);
                            }}
                          />
                          <span>{n}</span>
                        </label>
                      );
                    })}
                  </div>
                </Card>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" className="rounded-xl" type="button" onClick={() => setStep("basic")}>
                  Geri
                </Button>
                <Button className="rounded-xl" type="button" onClick={() => setStep("activity")}>
                  Aktivitelere Geç
                </Button>
              </div>
            </div>
          ) : null}

          {step === "activity" ? (
            <div className="space-y-6">
              <div className="flex items-center gap-2 border-b border-slate-100 pb-3 dark:border-slate-800">
                <div className="grid h-6 w-6 place-items-center rounded-full bg-[var(--panel-primary)] text-xs font-bold text-white">3</div>
                <div className="text-lg font-bold">Aktivite Seçimi</div>
              </div>

              <div className="text-sm text-slate-500 dark:text-slate-400">Birden fazla aktivite seçebilirsin.</div>

              <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
                <ActivityCard
                  active={activities.includes("meditasyon")}
                  title="Meditasyon"
                  description="Rehberli meditasyon ve farkındalık çalışmaları"
                  icon={<Sparkles className="h-6 w-6" />}
                  onClick={() => toggleActivity("meditasyon")}
                />
                <ActivityCard
                  active={activities.includes("nefes")}
                  title="Nefes"
                  description="Nefes ritmi ve sinir sistemi dengeleme"
                  icon={<AirVent className="h-6 w-6" />}
                  onClick={() => toggleActivity("nefes")}
                />
                <ActivityCard
                  active={activities.includes("rituel")}
                  title="Ritüel"
                  description="Niyet, yazma ve dönüşüm pratikleri"
                  icon={<Wand2 className="h-6 w-6" />}
                  onClick={() => toggleActivity("rituel")}
                />
                <ActivityCard
                  active={activities.includes("diyet")}
                  title="Diyet"
                  description="Beslenme düzeni ve günlük takip"
                  icon={<UtensilsCrossed className="h-6 w-6" />}
                  onClick={() => toggleActivity("diyet")}
                />
                <ActivityCard
                  active={activities.includes("sohbet")}
                  title="Sohbet"
                  description="Günün içgörülerini paylaşma alanı"
                  icon={<Users2 className="h-6 w-6" />}
                  onClick={() => toggleActivity("sohbet")}
                />
              </div>

              <div className="flex justify-between">
                <Button variant="outline" className="rounded-xl" type="button" onClick={() => setStep("filters")}>
                  Geri
                </Button>
                <Button
                  className="rounded-xl"
                  type="button"
                  onClick={() => {
                    setTasks((prev) =>
                      prev.length
                        ? prev
                        : buildDefaultTasksFromActivities(
                            activities,
                            selectedSuggestionByActivity,
                            activitySuggestionById,
                            activitySuggestionsByActivity,
                          ),
                    );
                    setStep("tasks");
                  }}
                >
                  Görevlere Geç
                </Button>
              </div>
            </div>
          ) : null}

          {step === "tasks" ? (
            <div className="space-y-6">
              <div className="flex items-center gap-2 border-b border-slate-100 pb-3 dark:border-slate-800">
                <div className="grid h-6 w-6 place-items-center rounded-full bg-[var(--panel-primary)] text-xs font-bold text-white">4</div>
                <div className="text-lg font-bold">Görevler</div>
              </div>

              <div className="text-sm text-slate-500 dark:text-slate-400">
                Her görev için ayrı sıklık ve tahmini süre belirleyebilirsin. Böylece çember planı daha gerçekçi olur.
              </div>

              {karmicDebtEnabled && karmicDebtNumbers.length ? (
                <div className="space-y-4 rounded-xl border border-[var(--panel-primary)]/20 bg-[var(--panel-primary)]/5 p-4">
                  <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    Seçili Karmik Borçlara özel öneriler ({karmicDebtNumbers.join(", ")})
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">
                    Öneriler görev adımına taşındı. Arama yapıp uygun öneriyi seçerek görevini hızlıca ekleyebilirsin.
                  </div>

                  <div className="space-y-4">
                    {activities.map((activity) => {
                      const suggestions = activitySuggestionsByActivity[activity] ?? [];
                      if (!suggestions.length || activity === "sohbet") return null;

                      const suggestionSearch = (suggestionSearchByActivity[activity] ?? "").trim().toLocaleLowerCase("tr-TR");
                      const filteredSuggestions = suggestionSearch
                        ? suggestions.filter((suggestion) => `${suggestion.title} ${suggestion.description}`.toLocaleLowerCase("tr-TR").includes(suggestionSearch))
                        : suggestions;

                      return (
                        <div key={`suggestion-${activity}`} className="space-y-2">
                          <div className="text-sm font-bold text-slate-900 dark:text-slate-100">{ACTIVITY_LABELS[activity]} Önerileri</div>
                          <Input
                            value={suggestionSearchByActivity[activity] ?? ""}
                            onChange={(event) => setSuggestionSearchByActivity((prev) => ({ ...prev, [activity]: event.target.value }))}
                            placeholder={`${ACTIVITY_LABELS[activity]} önerilerinde ara...`}
                            className="h-9"
                          />

                          {filteredSuggestions.length ? (
                            <div className="grid gap-2 md:grid-cols-2">
                              {filteredSuggestions.map((suggestion) => {
                                const selected = selectedSuggestionByActivity[activity] === suggestion.id;
                                return (
                                  <div
                                    key={suggestion.id}
                                    className={cn(
                                      "rounded-xl border p-3 transition",
                                      selected
                                        ? "border-[var(--panel-primary)] bg-[var(--panel-primary)]/10"
                                        : "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/40",
                                    )}
                                  >
                                    <div className="text-xs font-semibold text-[var(--panel-primary)]">Karmik Borç {suggestion.karmicCodes.join(", ")}</div>
                                    <div className="mt-1 text-sm font-bold text-slate-900 dark:text-slate-100">{suggestion.title}</div>
                                    <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">{suggestion.description}</div>
                                    {suggestion.durationMinutes ? (
                                      <div className="mt-1 text-[11px] font-medium text-slate-500">Süre: {suggestion.durationMinutes} dk</div>
                                    ) : null}

                                    {suggestion.imageUrl ? (
                                      <img src={suggestion.imageUrl} alt={suggestion.title} className="mt-2 h-24 w-full rounded-md object-cover" loading="lazy" />
                                    ) : null}
                                    {hasDirectAudioUrl(suggestion.audioUrl) ? (
                                      <audio controls preload="none" className="mt-2 h-8 w-full" src={suggestion.audioUrl}>
                                        Tarayıcın ses önizlemeyi desteklemiyor.
                                      </audio>
                                    ) : null}
                                    {hasDirectVideoUrl(suggestion.videoUrl) ? (
                                      <video controls preload="none" className="mt-2 h-28 w-full rounded-md object-cover" src={suggestion.videoUrl}>
                                        Tarayıcın video önizlemeyi desteklemiyor.
                                      </video>
                                    ) : null}

                                    <Button
                                      type="button"
                                      variant="outline"
                                      className="mt-2 rounded-lg"
                                      onClick={() => appendTaskFromSuggestion(suggestion)}
                                    >
                                      Öneriyi göreve ekle
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="rounded-lg border border-dashed border-slate-200 bg-white/70 px-3 py-4 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-900/30 dark:text-slate-400">
                              Aradığın kelimeye uygun öneri bulunamadı.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div className="space-y-3 rounded-xl border border-[var(--panel-primary)]/20 bg-[var(--panel-primary)]/5 p-4">
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">Hazır Programlardan Görev Ekle</div>
                <div className="text-xs text-slate-600 dark:text-slate-400">Admin panelinde tanımladığın meditasyon, nefes, ritüel ve diyet programlarından seçebilirsin.</div>
                <div className="space-y-3">
                  {activities.map((activity) => {
                    if (activity === "sohbet") return null;

                    const programs = programOptionsByActivity[activity] ?? [];
                    const searchTerm = (programSearchByActivity[activity] ?? "").trim().toLocaleLowerCase("tr-TR");
                    const filteredPrograms = searchTerm
                      ? programs.filter((program) => `${program.title} ${program.description}`.toLocaleLowerCase("tr-TR").includes(searchTerm))
                      : programs;
                    const visibleCount = Math.max(PROGRAM_LIST_PAGE_SIZE, programVisibleCountByActivity[activity] ?? PROGRAM_LIST_PAGE_SIZE);
                    const visiblePrograms = filteredPrograms.slice(0, visibleCount);
                    const canLoadMore = filteredPrograms.length > visibleCount;

                    return (
                      <div key={`program-${activity}`} className="space-y-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">{ACTIVITY_LABELS[activity]} Programları</div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">
                            {visiblePrograms.length}/{filteredPrograms.length} gösteriliyor
                          </div>
                        </div>

                        <Input
                          value={programSearchByActivity[activity] ?? ""}
                          onChange={(event) => {
                            const value = event.target.value;
                            setProgramSearchByActivity((prev) => ({ ...prev, [activity]: value }));
                            setProgramVisibleCountByActivity((prev) => ({ ...prev, [activity]: PROGRAM_LIST_PAGE_SIZE }));
                          }}
                          placeholder={`${ACTIVITY_LABELS[activity]} programlarında ara...`}
                          className="h-9"
                        />

                        {visiblePrograms.length ? (
                          <div className="grid gap-2 md:grid-cols-2">
                            {visiblePrograms.map((program) => (
                              <button
                                key={program.id}
                                type="button"
                                onClick={() => appendTaskFromProgram(activity, program)}
                                className="rounded-lg border border-slate-200 bg-white p-3 text-left transition hover:border-[var(--panel-primary)]/50 hover:bg-[var(--panel-primary)]/5 dark:border-slate-700 dark:bg-slate-900/40"
                              >
                                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{program.title}</div>
                                <div className="mt-1 line-clamp-2 text-xs text-slate-600 dark:text-slate-400">{program.description}</div>
                                {program.imageUrl ? (
                                  <img src={program.imageUrl} alt={program.title} className="mt-2 h-24 w-full rounded-md object-cover" loading="lazy" />
                                ) : null}
                                {hasDirectAudioUrl(program.audioUrl) ? (
                                  <audio controls preload="none" className="mt-2 h-8 w-full" src={program.audioUrl}>
                                    Tarayıcın ses önizlemeyi desteklemiyor.
                                  </audio>
                                ) : null}
                                {hasDirectVideoUrl(program.videoUrl) ? (
                                  <video controls preload="none" className="mt-2 h-28 w-full rounded-md object-cover" src={program.videoUrl}>
                                    Tarayıcın video önizlemeyi desteklemiyor.
                                  </video>
                                ) : null}
                                <div className="mt-2 text-[11px] font-medium text-[var(--panel-primary)]">Göreve ekle</div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="rounded-lg border border-dashed border-slate-200 bg-white/70 px-3 py-4 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-900/30 dark:text-slate-400">
                            Bu numerolojik filtreye uygun hazır program bulunamadı.
                          </div>
                        )}

                        {canLoadMore ? (
                          <Button
                            type="button"
                            variant="outline"
                            className="rounded-lg"
                            onClick={() =>
                              setProgramVisibleCountByActivity((prev) => ({
                                ...prev,
                                [activity]: (prev[activity] ?? PROGRAM_LIST_PAGE_SIZE) + PROGRAM_LIST_PAGE_SIZE,
                              }))
                            }
                          >
                            Daha fazla göster
                          </Button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3">
                {tasks.map((t, idx) => (
                  <Card key={t.id} className="rounded-xl border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/30">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={t.active}
                          onCheckedChange={(v) => setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, active: Boolean(v) } : x)))}
                        />
                        <div className="text-sm font-bold">Görev {idx + 1}</div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-lg"
                          disabled={idx === 0}
                          onClick={() =>
                            setTasks((prev) => {
                              if (idx === 0) return prev;
                              const copy = [...prev];
                              const a = copy[idx - 1];
                              copy[idx - 1] = copy[idx];
                              copy[idx] = a;
                              return copy;
                            })
                          }
                        >
                          ↑
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-lg"
                          disabled={idx === tasks.length - 1}
                          onClick={() =>
                            setTasks((prev) => {
                              if (idx >= prev.length - 1) return prev;
                              const copy = [...prev];
                              const a = copy[idx + 1];
                              copy[idx + 1] = copy[idx];
                              copy[idx] = a;
                              return copy;
                            })
                          }
                        >
                          ↓
                        </Button>
                        <Button type="button" variant="destructive" className="rounded-lg" onClick={() => setTasks((prev) => prev.filter((x) => x.id !== t.id))}>
                          Sil
                        </Button>
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3">
                      <Input
                        value={t.title}
                        onChange={(e) => setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, title: e.target.value } : x)))}
                        placeholder="Görev adı"
                        className="h-10 rounded-xl"
                      />
                      <Textarea
                        value={t.description}
                        onChange={(e) => setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, description: e.target.value } : x)))}
                        placeholder="Görev açıklaması (ne yapılacak?)"
                        rows={2}
                      />
                      <div className="grid gap-2 md:grid-cols-[1fr_auto]">
                        <Input
                          value={t.imageUrl ?? ""}
                          onChange={(e) => setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, imageUrl: e.target.value } : x)))}
                          placeholder="Görev görseli URL (opsiyonel)"
                          className="h-10 rounded-xl"
                        />
                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-[var(--panel-primary)]/35 px-3 text-xs font-semibold text-[var(--panel-primary)] hover:bg-[var(--panel-primary)]/5">
                          <ImagePlus className="h-4 w-4" /> Görsel Seç
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.currentTarget.files?.[0] ?? null;
                              e.currentTarget.value = "";
                              if (!file) return;
                              void readFileAsDataUrl(file)
                                .then((dataUrl) => {
                                  setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, imageUrl: dataUrl } : x)));
                                })
                                .catch((err) => {
                                  const msg = err instanceof Error ? err.message : "Görsel yüklenemedi";
                                  toast({ title: "Görsel yüklenemedi", description: msg, variant: "destructive" });
                                });
                            }}
                          />
                        </label>
                      </div>
                      {String(t.imageUrl ?? "").trim() ? (
                        <div className="h-24 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-slate-800">
                          <img src={String(t.imageUrl)} alt="Görev görseli" className="h-full w-full object-cover" />
                        </div>
                      ) : null}
                      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                        <Select
                          value={t.kind}
                          onValueChange={(v) => setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, kind: v as CircleTaskKind } : x)))}
                        >
                          <SelectTrigger className="rounded-xl">
                            <SelectValue placeholder="Görev türü" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="meditasyon">Meditasyon</SelectItem>
                            <SelectItem value="nefes">Nefes</SelectItem>
                            <SelectItem value="diyet">Diyet</SelectItem>
                            <SelectItem value="rituel">Ritüel</SelectItem>
                            <SelectItem value="sohbet">Sohbet</SelectItem>
                            <SelectItem value="ozel">Özel</SelectItem>
                          </SelectContent>
                        </Select>

                        <Select
                          value={t.frequency}
                          onValueChange={(v) => setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, frequency: v as TaskFrequency } : x)))}
                        >
                          <SelectTrigger className="rounded-xl">
                            <SelectValue placeholder="Sıklık" />
                          </SelectTrigger>
                          <SelectContent>
                            {TASK_FREQUENCY_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select
                          value={String(t.durationMinutes)}
                          onValueChange={(v) => {
                            const duration = Number(v);
                            setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, durationMinutes: duration } : x)));
                          }}
                        >
                          <SelectTrigger className="rounded-xl">
                            <SelectValue placeholder="Tahmini süre" />
                          </SelectTrigger>
                          <SelectContent>
                            {TASK_DURATION_OPTIONS.map((duration) => (
                              <SelectItem key={duration} value={String(duration)}>
                                {duration} dk
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Input
                          value={String(t.points)}
                          onChange={(e) => {
                            const n = Math.max(0, Math.min(100, Math.trunc(Number(e.target.value) || 0)));
                            setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, points: n } : x)));
                          }}
                          placeholder="Puan"
                          className="h-10 rounded-xl"
                        />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-xl"
                    onClick={() =>
                      setTasks((prev) => [
                        ...prev,
                        { id: makeDraftId("task"), title: "", description: "", imageUrl: "", kind: "ozel", points: 1, frequency: "daily", durationMinutes: 10, active: true },
                      ])
                    }
                  >
                    Yeni görev ekle
                  </Button>

                  {activities.map((activity) => {
                    const selectedId = selectedSuggestionByActivity[activity];
                    const selected = selectedId ? activitySuggestionById.get(selectedId) ?? null : null;
                    const fallback = activitySuggestionsByActivity[activity]?.[0] ?? null;
                    const nextTask = buildTaskFromActivity(activity, selected ?? fallback);

                    return (
                      <Button
                        key={activity}
                        type="button"
                        variant="secondary"
                        className="rounded-xl"
                        onClick={() => setTasks((prev) => [...prev, { id: makeDraftId("task"), ...nextTask, active: true }])}
                      >
                        {ACTIVITY_LABELS[activity]} görevi
                      </Button>
                    );
                  })}
                </div>

                <Button className="rounded-xl" type="button" onClick={() => setStep("preview")}>
                  Önizlemeye Geç
                </Button>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" className="rounded-xl" type="button" onClick={() => setStep("activity")}>
                  Geri
                </Button>
                <Button className="rounded-xl" type="button" onClick={() => setStep("preview")}>
                  Önizlemeye Geç
                </Button>
              </div>
            </div>
          ) : null}

          {step === "preview" ? (
            <div className="space-y-6">
              <div className="flex items-center gap-2 border-b border-slate-100 pb-3 dark:border-slate-800">
                <div className="grid h-6 w-6 place-items-center rounded-full bg-[var(--panel-primary)] text-xs font-bold text-white">5</div>
                <div className="text-lg font-bold">Önizleme</div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/30">
                <div className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Çember Kartı</div>
                <div className="grid gap-4 md:grid-cols-[180px_1fr]">
                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-200 dark:border-slate-700 dark:bg-slate-800">
                    <div className="h-full min-h-[120px]">
                      {imageUrl.trim() ? <img src={imageUrl} alt="Çember kartı" className="h-full w-full object-cover" /> : null}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-lg font-black text-slate-900 dark:text-white">{name.trim() || "Yeni Çember"}</div>
                    <div className="text-sm text-slate-600 dark:text-slate-300">{circleType}</div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="rounded-full bg-white px-2 py-1 font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                        {circleDuration === "unlimited" ? "Süresiz" : `${circleDuration} gün`}
                      </span>
                      <span className="rounded-full bg-white px-2 py-1 font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                        Katılım: {participationRuleLabel}
                      </span>
                      <span className="rounded-full bg-white px-2 py-1 font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                        {tasks.filter((t) => t.active).length} görev
                      </span>
                    </div>
                    <div className="text-xs font-semibold text-slate-600 dark:text-slate-300">Çember Enerji Seviyesi: %{estimatedEnergy}</div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/30">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Aktif Kurallar</div>
                <ul className="mt-3 space-y-2">
                  {activeRulesLabel.map((t) => (
                    <li key={t} className="flex items-center gap-2 text-sm">
                      <span className="h-2 w-2 rounded-full bg-emerald-500" />
                      <span className="text-slate-700 dark:text-slate-200">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/30">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                  <Flame className="h-4 w-4 text-[var(--panel-primary)]" />
                  Bağımlılık Mekanikleri (Sosyal + Numerolojik)
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-950/50">
                    <span>Çember Enerji Seviyesi</span>
                    <Checkbox checked={energySystemEnabled} onCheckedChange={(v) => setEnergySystemEnabled(Boolean(v))} />
                  </label>
                  <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-950/50">
                    <span>Günlük Seri (Streak)</span>
                    <Checkbox checked={streakEnabled} onCheckedChange={(v) => setStreakEnabled(Boolean(v))} />
                  </label>
                  <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-950/50">
                    <span>Çember Lider Tablosu</span>
                    <Checkbox checked={leaderboardEnabled} onCheckedChange={(v) => setLeaderboardEnabled(Boolean(v))} />
                  </label>
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-950/50">
                    <div className="mb-1 flex items-center justify-between">
                      <span>Çember Ritüel Saati</span>
                      <Checkbox checked={ritualHourEnabled} onCheckedChange={(v) => setRitualHourEnabled(Boolean(v))} />
                    </div>
                    <Select value={ritualHour} onValueChange={setRitualHour} disabled={!ritualHourEnabled}>
                      <SelectTrigger className="h-8 rounded-lg text-xs">
                        <SelectValue placeholder="Saat seç" />
                      </SelectTrigger>
                      <SelectContent>
                        {RITUAL_HOUR_OPTIONS.map((hour) => (
                          <SelectItem key={hour} value={hour}>
                            {hour}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-950/50">
                    <div className="mb-1 flex items-center justify-between">
                      <span>Enerji Alanı (Aura)</span>
                      <Checkbox checked={auraEnabled} onCheckedChange={(v) => setAuraEnabled(Boolean(v))} />
                    </div>
                    <Select value={auraType} onValueChange={(v) => setAuraType(v as CircleAura)} disabled={!auraEnabled}>
                      <SelectTrigger className="h-8 rounded-lg text-xs">
                        <SelectValue placeholder="Aura seç" />
                      </SelectTrigger>
                      <SelectContent>
                        {CIRCLE_AURA_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label} • {option.hz} Hz
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-950/50">
                    <span>Çember Paylaşım Alanı</span>
                    <Checkbox checked={sharingAreaEnabled} onCheckedChange={(v) => setSharingAreaEnabled(Boolean(v))} />
                  </label>
                  <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-950/50">
                    <span>Günün Enerjisi</span>
                    <Checkbox checked={dailyEnergyEnabled} onCheckedChange={(v) => setDailyEnergyEnabled(Boolean(v))} />
                  </label>
                  <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-950/50">
                    <span>Çember Rozetleri</span>
                    <Checkbox checked={badgeSystemEnabled} onCheckedChange={(v) => setBadgeSystemEnabled(Boolean(v))} />
                  </label>
                  <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-950/50">
                    <span>Çember Eşleşme Skoru</span>
                    <Checkbox checked={matchScoreEnabled} onCheckedChange={(v) => setMatchScoreEnabled(Boolean(v))} />
                  </label>
                  <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-950/50">
                    <span>Çember Evrimi</span>
                    <Checkbox checked={evolutionEnabled} onCheckedChange={(v) => setEvolutionEnabled(Boolean(v))} />
                  </label>
                  <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-950/50">
                    <span>Otomatik Çember Önerisi</span>
                    <Checkbox checked={autoSuggestionEnabled} onCheckedChange={(v) => setAutoSuggestionEnabled(Boolean(v))} />
                  </label>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  {energySystemEnabled ? (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                      <div className="font-semibold">Enerji: %{estimatedEnergy}</div>
                      <div className="mt-1">Bugün 3 kişi görev yapmazsa enerji %{Math.max(10, estimatedEnergy - 11)} seviyesine düşebilir.</div>
                    </div>
                  ) : null}
                  {streakEnabled ? (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900">
                      <div className="flex items-center gap-1 font-semibold"><Flame className="h-3 w-3" /> Meditasyon Serisi: {sampleUserStreak} gün</div>
                      <div className="mt-1">Seri bozulursa kullanıcı ertesi gün geri dönmeye daha motive olur.</div>
                    </div>
                  ) : null}
                  {leaderboardEnabled ? (
                    <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                      <div className="flex items-center gap-1 font-semibold"><Trophy className="h-3 w-3" /> Çember Liderleri</div>
                      <div className="mt-1">1. Ayşe • 12 görev · 2. Mehmet • 10 görev · 3. Elif • 8 görev</div>
                    </div>
                  ) : null}
                  {ritualHourEnabled ? (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
                      <div className="flex items-center gap-1 font-semibold"><Clock3 className="h-3 w-3" /> Bugünkü Ritüel: {ritualHour}</div>
                      <div className="mt-1">Saat geldiğinde “Çember ritüeli başladı” bildirimi gönderilir.</div>
                    </div>
                  ) : null}
                  {auraEnabled ? (
                    <div className="rounded-xl border border-violet-200 bg-violet-50 p-3 text-xs text-violet-900">
                      <div className="font-semibold">Aura: {auraConfig.label} • {auraConfig.hz} Hz</div>
                      <div className="mt-1">Numerolojik frekans kart üzerinde görünür ve ruhsal hissi güçlendirir.</div>
                    </div>
                  ) : null}
                  {badgeSystemEnabled ? (
                    <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-950/50 dark:text-slate-200">
                      <div className="flex items-center gap-1 font-semibold"><BadgeCheck className="h-3 w-3" /> Rozetler</div>
                      <div className="mt-1">{BADGE_PREVIEWS.join(" • ")}</div>
                    </div>
                  ) : null}
                  {dailyEnergyEnabled ? (
                    <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-3 text-xs text-cyan-900">
                      <div className="font-semibold">Günün Enerjisi: {((new Date().getDate() % 9) || 9)}</div>
                      <div className="mt-1">İçe dönüş ve farkındalık odaklı mini mesaj otomatik gösterilir.</div>
                    </div>
                  ) : null}
                  {matchScoreEnabled ? (
                    <div className="rounded-xl border border-fuchsia-200 bg-fuchsia-50 p-3 text-xs text-fuchsia-900">
                      <div className="font-semibold">Bu çemberle uyum skoru: %{sampleMatchScore}</div>
                      <div className="mt-1">Üye katılmadan önce bu skor gösterilir.</div>
                    </div>
                  ) : null}
                  {evolutionEnabled ? (
                    <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-900">
                      <div className="font-semibold">{circleEvolutionLevel}</div>
                      <div className="mt-1">Enerji arttıkça çember otomatik seviyelenir.</div>
                    </div>
                  ) : null}
                  {autoSuggestionEnabled ? (
                    <div className="rounded-xl border border-teal-200 bg-teal-50 p-3 text-xs text-teal-900">
                      <div className="flex items-center gap-1 font-semibold"><Radio className="h-3 w-3" /> Otomatik Çember Önerisi</div>
                      <div className="mt-1">Senin enerjine uygun 3 çember bulundu mesajı ana akışta gösterilir.</div>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="rounded-2xl border border-[var(--panel-primary)]/25 bg-[var(--panel-primary)]/5 p-4">
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Numerolojik Davet</div>
                <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">Çember oluşturulduktan sonra uyumlu kişileri davet panelinden hızlıca davet edebilirsin.</div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/40">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">Yakındaki Ritüeller haritasında göster</div>
                    <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Konum paylaşırsan çember oluştuğunda haritaya otomatik ritüel pini düşer.
                    </div>
                  </div>
                  <Checkbox checked={shareLocationEnabled} onCheckedChange={(v) => setShareLocationEnabled(Boolean(v))} />
                </div>

                {shareLocationEnabled ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button type="button" variant="outline" className="rounded-xl" onClick={onShareLocation} disabled={locationBusy}>
                      <MapPin className="mr-2 h-4 w-4" />
                      {locationBusy ? "Konum alınıyor..." : sharedLocation ? "Konumu güncelle" : "Konumumu al"}
                    </Button>
                    {sharedLocation ? (
                      <span className="text-xs text-emerald-600 dark:text-emerald-400">
                        {sharedLocation.lat.toFixed(5)}, {sharedLocation.lng.toFixed(5)}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-500 dark:text-slate-400">Haritaya düşmesi için bir kez konum al.</span>
                    )}
                  </div>
                ) : null}
              </div>

              <div className="flex justify-between">
                <Button variant="outline" className="rounded-xl" type="button" onClick={() => setStep("tasks")}>
                  Geri
                </Button>
                <Button
                  className="rounded-xl"
                  disabled={!canCreate || createBusy}
                  type="button"
                  onClick={async () => {
                    if (createBusy) return;

                    const cleanName = name.trim();
                    if (!cleanName) return;

                    if (!user?.id) {
                      toast({
                        title: "Giriş gerekli",
                        description: "Çemberi veritabanına kaydetmek için giriş yapmalısın.",
                        variant: "destructive",
                      });
                      return;
                    }

                    setCreateBusy(true);
                    try {
                      const startsAtIso = new Date().toISOString();
                      const durationDays = circleDuration === "unlimited" ? null : Number(circleDuration);
                      const endsAtIso = durationDays ? new Date(Date.now() + durationDays * 24 * 60 * 60 * 1000).toISOString() : null;

                      const socialFeatures = {
                        energySystemEnabled,
                        streakEnabled,
                        leaderboardEnabled,
                        ritualHour: ritualHourEnabled ? ritualHour : null,
                        aura: auraEnabled ? { type: auraType, label: auraConfig.label, hz: auraConfig.hz } : null,
                        sharingAreaEnabled,
                        dailyEnergyEnabled,
                        badgeSystemEnabled,
                        matchScoreEnabled,
                        evolutionEnabled,
                        autoSuggestionEnabled,
                        participationRule,
                      };

                      const created = draftCircleId
                        ? await publishCircleDraftInDb({
                            circleId: draftCircleId,
                            name: cleanName,
                            rules,
                            description,
                            activity: `${circleType} | ${activityValue}`,
                            activities,
                            circleType,
                            status: "active",
                            showOnRitualMap: shareLocationEnabled,
                            startsAt: startsAtIso,
                            endsAt: endsAtIso,
                            imageUrl: imageUrl.trim() || undefined,
                            durationDays,
                            maxParticipants: null,
                            socialFeatures,
                          })
                        : await createCircleInDb({
                            name: cleanName,
                            rules,
                            description,
                            activity: `${circleType} | ${activityValue}`,
                            activities,
                            circleType,
                            status: "active",
                            showOnRitualMap: shareLocationEnabled,
                            startsAt: startsAtIso,
                            endsAt: endsAtIso,
                            imageUrl: imageUrl.trim() || undefined,
                            meta: {
                              durationDays,
                              maxParticipants: null,
                              socialFeatures,
                            },
                          });

                      const activeTasks = tasks.filter((t) => t.active && t.title.trim());
                      if (activeTasks.length === 0) {
                        throw new Error("En az 1 görev eklemelisin");
                      }

                      const existingTasks = await listCircleTasks(created.id).catch(() => []);
                      for (const existingTask of existingTasks) {
                        await deleteCircleTaskInDb({ circleId: created.id, taskId: existingTask.id });
                      }

                      for (let i = 0; i < activeTasks.length; i++) {
                        const t = activeTasks[i];
                        const scheduleLine = `Plan: ${TASK_FREQUENCY_LABELS[t.frequency]} • ${t.durationMinutes} dk`;
                        const taskImageUrl = toPublicUrl(String(t.imageUrl ?? "").trim());
                        const imageLine = taskImageUrl ? `Görsel: ${taskImageUrl}` : "";
                        const composedDescription = [scheduleLine, t.description.trim(), imageLine].filter(Boolean).join("\n");

                        await createCircleTaskInDb({
                          circleId: created.id,
                          title: t.title,
                          description: composedDescription,
                          kind: t.kind,
                          points: t.points,
                          sortOrder: i,
                          active: true,
                        });
                      }

                      if (shareLocationEnabled && sharedLocation) {
                        const firstActiveTask = activeTasks[0];
                        const mapTitle = firstActiveTask?.title?.trim() || `${cleanName} Ritüeli`;
                        const mapDescription = firstActiveTask?.description?.trim() || description.trim();

                        await createRitualSessionFromCircle({
                          circleId: created.id,
                          title: mapTitle,
                          description: mapDescription,
                          lat: sharedLocation.lat,
                          lng: sharedLocation.lng,
                        });
                      }

                      navigate(`/panel/cember/${created.id}`);
                    } catch (err) {
                      const msg = err instanceof Error ? err.message : "Çember oluşturulamadı";
                      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
                      setCreateBusy(false);
                    }
                  }}
                >
                  {createBusy ? (isEditMode ? "Güncelleniyor..." : "Oluşturuluyor...") : isEditMode ? "Çemberi Güncelle" : "Çemberi Oluştur"}
                </Button>
              </div>
            </div>
          ) : null}
        </main>
      </div>
    </div>
  );
}
